import React, { Component } from 'react';

import EventInclusionsEditor, { emptyRow } from './EventInclusionsEditor';
import { availability, aperturaDelSalon, mesasOcupadasDelError } from '../utils/activitiesApi';
import { createPrivateEvent, listEvents, getEvent } from '../utils/eventsApi';
import { get } from '../utils/apiClient';
import { getSelectedBranchId, getCachedBranches } from '../utils/branch';
import { navigateTo } from '../utils/navigation';
import {
    LIMITS, PAYMENT_METHODS, isStartsAt, peopleCount, proposeTables, keepFreeTables, tablesSummary, includedPreview,
    copyableEvents, prefillFromEvent, cedulaDigits, applyClientLookup, validatePrivateEvent, errorsByField,
    missingBlocks, buildPrivateEventBody, totalText, saleErrorNotice, rejectionText, jornadaPasada, PAST_DATE_TEXT,
} from '../utils/privateEvent';

// «Evento privado» — el alta de un cumpleaños o un festejo con todo pago, en UNA pantalla.
//
// Por debajo es lo mismo que se armaba en cuatro pantallas (evento, tipo de entrada con «Qué
// incluye», mesas del salón, publicar y venta presencial): acá se carga todo junto y «Crear
// evento» lo manda en un solo pedido (`POST /manage-events/private`). El evento nace publicado y
// sólo para el mostrador: la web no lo abre ni lo vende, y no lleva póster.
//
// Contrato de datos, que es de donde salen los defectos posibles:
//
//   - La jornada NO se calcula acá. La fecha y hora viajan crudas, tanto a la disponibilidad
//     (`availability({ startsAt })`) como al alta; qué día de salón es y si abre a esa hora lo
//     dice el servidor.
//   - El alta es todo o nada, pero la venta va después y puede fallar sola: la respuesta trae
//     siempre `event` y, además, `sale` o `saleError`. Con `saleError` el evento EXISTE: se va a
//     su pantalla con el aviso, nunca se reintenta desde acá (la venta no es idempotente). Si
//     además trae `warnings` (avisos del salón, `[{ label, reason }]`), viajan a esa pantalla.
//   - Un rechazo no deja nada creado y el formulario queda como estaba, con el motivo. El de
//     mesas (7304) trae la lista en `err.body.data`, no en el mensaje.
//
// Lo que se decide sin pedir nada (qué mesas proponer, qué falta, qué viaja) vive en
// `utils/privateEvent.js`, con sus tests; este componente dibuja y llama.
class FormPrivateEventComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            title: '',
            startsAt: '',
            people: '',
            price: '',
            notes: '',
            // Sucursal: la que está elegida arriba. `branches` se carga sólo si no hay ninguna
            // (el selector en «Todas»), que es el único caso en que se pregunta.
            branchId: '',
            branches: null,
            branchesError: null,
            // Qué incluye: los renglones del editor y de dónde se copiaron. Arranca con un
            // renglón en blanco ya abierto: al menos uno hace falta siempre, y el primer
            // «Agregar renglón» era un click que no aportaba nada. Copiar de un evento anterior
            // lo reemplaza sin preguntar (nadie lo tocó).
            rows: [emptyRow()],
            rowsTouched: false,
            events: null,          // los eventos anteriores que declaran algo; null mientras carga
            eventsError: false,
            copyFrom: '',
            copying: false,
            copyError: null,
            copiedTitle: null,     // de qué evento salieron los renglones precargados
            priceFromCopy: null,   // el precio que puso la última copia (no lo tipeó nadie)
            menu: null,
            menuError: false,
            // Mesas: la disponibilidad de esa fecha y hora, y las tildadas. Mientras el usuario
            // no toque una casilla (`tablesTouched`), la propuesta se recalcula sola.
            disponibilidad: null,
            cargandoMesas: false,
            errorMesas: null,
            tableIds: [],
            tablesTouched: false,
            mesasQuitadas: [],
            // Cobro y quién reserva.
            paymentMode: 'now',
            paymentMethod: 'efectivo',
            cedula: '',
            customerName: '',
            customerLastName: '',
            customerPhone: '',
            customerEmail: '',
            clientHint: null,
            datosCedula: '',
            // El envío.
            intentado: false,
            enviando: false,
            error: null,
            ocupadas: [],
        };
        this.card = React.createRef();
        this.mesasSeq = 0;
        this.copySeq = 0;
    }

    componentDidMount() {
        this.cargarSucursales();
        this.cargarEventos();
        if (this.state.rows.length > 0) this.fetchMenu();
    }

    componentDidUpdate() {
        if (this.state.rows.length > 0) this.fetchMenu();
    }

    componentWillUnmount() {
        this.unmounted = true;
        clearTimeout(this.mesasTimer);
    }

    // ── Sucursal ──────────────────────────────────────────────────────────────────────────
    //
    // La que el usuario tiene elegida en la barra de arriba. Se lee cada vez y no una sola al
    // montar: la barra la resuelve por su cuenta y puede terminar después que esta pantalla.
    sucursalElegida = () => {
        const selected = getSelectedBranchId();
        return selected && selected !== 'all' ? String(selected) : '';
    }

    branchId = () => this.sucursalElegida() || String(this.state.branchId || '');

    cargarSucursales = () => {
        if (this.sucursalElegida()) return;
        const usar = (branches) => {
            if (this.unmounted) return;
            // Con una sola no hay nada que preguntar.
            this.setState({ branches, branchId: branches.length === 1 ? String(branches[0].id) : '' });
        };
        const cached = getCachedBranches();
        if (cached != null) { usar(cached); return; }
        get('branch/mine')
            .then((data) => usar(Array.isArray(data) ? data : []))
            .catch((e) => { if (!this.unmounted) this.setState({ branches: [], branchesError: e.message }); });
    }

    // ── Qué incluye ───────────────────────────────────────────────────────────────────────
    //
    // La lista para «Copiar de un evento anterior…» es una comodidad: si falla, el formulario
    // sigue andando y los renglones se cargan a mano.
    cargarEventos = () => {
        listEvents()
            .then((data) => { if (!this.unmounted) this.setState({ events: copyableEvents(data && data.events) }); })
            .catch((error) => {
                console.error('No se pudieron cargar los eventos anteriores:', error);
                if (!this.unmounted) this.setState({ events: [], eventsError: true });
            });
    }

    // El menú ENTERO de cafetería, una sola vez y recién cuando hay algún renglón: es la vara
    // con la que el servidor valida un renglón por ítems (ver EventTicketTypesComponent).
    fetchMenu = () => {
        if (this.menuRequested) return;
        this.menuRequested = true;
        get('cafeteria/menu')
            .then((data) => { if (!this.unmounted) this.setState({ menu: data || [] }); })
            .catch((error) => {
                console.error('No se pudo cargar el menú de cafetería:', error);
                if (!this.unmounted) this.setState({ menu: [], menuError: true });
            });
    }

    // Si el menú no cargó se vuelve a pedir desde acá: «recargá la página» en esta pantalla
    // perdería todo lo que ya se cargó.
    reintentarMenu = () => {
        this.menuRequested = false;
        this.setState({ menu: null, menuError: false }, this.fetchMenu);
    }

    onChangeRows = (rows) => this.setState({ rows, rowsTouched: true });

    // Elegir un evento anterior precarga sus renglones y, si el precio está vacío, su precio
    // (un precio que puso una copia anterior y nadie tocó cuenta como vacío: se eligió otro
    // evento). Lo precargado se puede editar antes de crear. Sólo se pregunta si se van a pisar
    // renglones cargados a mano.
    onChangeCopyFrom = (e) => {
        const id = e.target.value;
        if (id === '') { this.setState({ copyFrom: '', copyError: null }); return; }
        const elegido = (this.state.events || []).find((event) => String(event.id) === String(id));
        if (this.state.rows.length > 0 && this.state.rowsTouched
            && !window.confirm(`¿Reemplazar lo que cargaste en «Qué incluye» por lo de «${elegido ? elegido.title : 'ese evento'}»?`)) {
            return;
        }
        const seq = ++this.copySeq;
        this.setState({ copyFrom: id, copying: true, copyError: null });
        getEvent(id)
            .then((data) => {
                if (seq !== this.copySeq || this.unmounted) return;
                const previo = prefillFromEvent(data && data.event);
                if (!previo) {
                    this.setState({ copying: false, copyFrom: '', copyError: 'No se pudo leer qué incluye ese evento. Probá con otro o cargalo a mano.' });
                    return;
                }
                // El desplegable vuelve a su lugar: es una acción, no un dato del formulario, y
                // así se puede volver a elegir el mismo evento para deshacer los cambios.
                this.setState((prev) => {
                    const sinPrecio = String(prev.price).trim() === '' || String(prev.price) === prev.priceFromCopy;
                    return {
                        copying: false,
                        copyFrom: '',
                        copiedTitle: elegido ? elegido.title : null,
                        rows: previo.rows,
                        rowsTouched: false,
                        ...(sinPrecio ? { price: previo.price, priceFromCopy: previo.price } : {}),
                    };
                });
            })
            .catch((error) => {
                if (seq !== this.copySeq || this.unmounted) return;
                this.setState({ copying: false, copyFrom: '', copyError: error.message });
            });
    }

    // ── Mesas ─────────────────────────────────────────────────────────────────────────────
    //
    // Cada cambio de fecha u hora vuelve a pedir la disponibilidad. Un <input> de fecha y hora
    // cambia de valor varias veces mientras se tipea, así que el pedido espera un instante; y
    // sólo se aplica la respuesta del ÚLTIMO (una vieja que llega tarde no pisa una nueva).
    onChangeStartsAt = (e) => {
        const startsAt = e.target.value;
        clearTimeout(this.mesasTimer);
        this.mesasSeq += 1;
        if (!isStartsAt(startsAt)) {
            this.setState({ startsAt, disponibilidad: null, cargandoMesas: false, errorMesas: null, tableIds: [], tablesTouched: false, mesasQuitadas: [] });
            return;
        }
        this.setState({ startsAt, cargandoMesas: true, errorMesas: null });
        this.mesasTimer = setTimeout(() => this.cargarMesas(startsAt), FormPrivateEventComponent.availabilityDelayMs);
    }

    cargarMesas = (startsAt) => {
        const seq = ++this.mesasSeq;
        this.setState({ cargandoMesas: true, errorMesas: null });
        return availability({ startsAt })
            .then((data) => {
                if (seq !== this.mesasSeq || this.unmounted) return;
                const apertura = aperturaDelSalon(data);
                const disponibilidad = {
                    tables: (data && data.tables) || [],
                    closedDay: !!(data && data.closedDay),
                    open: apertura.open,
                    closedReason: apertura.reason,
                    // La jornada que le tocó a esa hora y la jornada en curso, las dos del
                    // servidor: de compararlas sale si la fecha ya pasó (`jornadaPasada`).
                    date: (data && data.date) || null,
                    businessDate: (data && data.businessDate) || null,
                };
                this.setState((prev) => {
                    // Sin tocar las casillas, la propuesta se rehace. Con la selección hecha a
                    // mano se respeta, menos las que dejaron de estar libres.
                    if (!prev.tablesTouched) {
                        return { disponibilidad, cargandoMesas: false, tableIds: proposeTables(disponibilidad.tables, prev.people), mesasQuitadas: [] };
                    }
                    const quedan = keepFreeTables(disponibilidad.tables, prev.tableIds);
                    return { disponibilidad, cargandoMesas: false, tableIds: quedan.tableIds, mesasQuitadas: quedan.dropped };
                });
            })
            .catch((error) => {
                if (seq !== this.mesasSeq || this.unmounted) return;
                this.setState({ disponibilidad: null, cargandoMesas: false, errorMesas: error.message, tableIds: [], mesasQuitadas: [] });
            });
    }

    onChangePeople = (e) => {
        const people = e.target.value;
        this.setState((prev) => ({
            people,
            ...(prev.tablesTouched || !prev.disponibilidad ? {} : { tableIds: proposeTables(prev.disponibilidad.tables, people) }),
        }));
    }

    toggleTable = (tableId) => {
        const id = Number(tableId);
        this.setState((prev) => ({
            tablesTouched: true,
            mesasQuitadas: [],
            tableIds: prev.tableIds.includes(id) ? prev.tableIds.filter((t) => t !== id) : [...prev.tableIds, id],
        }));
    }

    proponerDeNuevo = () => {
        this.setState((prev) => ({
            tablesTouched: false,
            mesasQuitadas: [],
            tableIds: prev.disponibilidad ? proposeTables(prev.disponibilidad.tables, prev.people) : [],
        }));
    }

    // ── Quién reserva ─────────────────────────────────────────────────────────────────────
    //
    // Al salir de la cédula se busca al cliente y se completan los demás datos, con la regla del
    // modal «Venta presencial» (`applyClientLookup`). Va por /clients, que une la ficha de
    // facturación y la cuenta de la web.
    lookupCliente = () => {
        const cedula = cedulaDigits(this.state.cedula);
        if (!cedula) { this.setState({ clientHint: null }); return; }
        get(`clients/${encodeURIComponent(cedula)}`)
            .then((cliente) => {
                // Si la cédula cambió mientras se buscaba, esta respuesta es de otra persona.
                if (this.unmounted || cedulaDigits(this.state.cedula) !== cedula) return;
                this.setState((prev) => applyClientLookup(prev, cedula, cliente || null));
            })
            .catch((error) => {
                // La búsqueda es una comodidad: si falla, los datos se cargan a mano igual.
                console.error('No se pudo buscar el cliente:', error);
                if (!this.unmounted) this.setState({ clientHint: null });
            });
    }

    handleChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    }

    // ── Validación y envío ────────────────────────────────────────────────────────────────
    form = () => ({
        branchId: this.branchId(),
        title: this.state.title,
        startsAt: this.state.startsAt,
        people: this.state.people,
        price: this.state.price,
        rows: this.state.rows,
        tableIds: this.state.tableIds,
        notes: this.state.notes,
        paymentMode: this.state.paymentMode,
        paymentMethod: this.state.paymentMethod,
        cedula: this.state.cedula,
        customerName: this.state.customerName,
        customerLastName: this.state.customerLastName,
        customerPhone: this.state.customerPhone,
        customerEmail: this.state.customerEmail,
    })

    errores = () => validatePrivateEvent(this.form(), {
        availability: this.state.disponibilidad,
        loadingTables: this.state.cargandoMesas,
        tablesError: this.state.errorMesas,
        // Si el menú no cargó no se puede saber qué ítems siguen existiendo: decide el servidor.
        menu: this.state.menuError ? null : this.state.menu,
    })

    // Lleva la vista a lo primero que falta: el botón está al pie y el primer campo, arriba.
    irAlPrimerError = () => {
        const nodo = this.card.current && this.card.current.querySelector('.is-invalid, .invalid-feedback');
        if (!nodo) return;
        if (typeof nodo.scrollIntoView === 'function') nodo.scrollIntoView({ block: 'center' });
        if (nodo.classList.contains('is-invalid') && typeof nodo.focus === 'function') nodo.focus();
    }

    submit = () => {
        // El botón ya está deshabilitado mientras se envía; esto es el cinturón: un segundo
        // pedido vendería las entradas otra vez.
        if (this.enviando) return;
        if (this.errores().length > 0) {
            this.setState({ intentado: true, error: null, ocupadas: [] }, this.irAlPrimerError);
            return;
        }
        this.enviando = true;
        this.setState({ intentado: true, enviando: true, error: null, ocupadas: [] });
        // `then(bien, mal)` y no `.catch`: lo que falle DESPUÉS de crear (la navegación) no puede
        // terminar mostrando un rechazo y un botón habilitado sobre un evento que ya existe.
        createPrivateEvent(buildPrivateEventBody(this.form())).then(
            (data) => {
                // No se vuelve a habilitar nada: el evento ya existe y lo que sigue es otra
                // pantalla. Con `saleError` se llega con el aviso; la venta se reintenta allá.
                const id = data && data.event && data.event.id;
                if (!id) { navigateTo('/events'); return; }
                const state = {};
                if (data.saleError) state.privateEventNotice = saleErrorNotice(data.saleError);
                // Los avisos del salón al reservar las mesas ("6-C tiene una reserva a las 21"),
                // si el servidor los manda: se muestran allá como al publicar.
                if (Array.isArray(data.warnings) && data.warnings.length > 0) state.activityWarnings = data.warnings;
                navigateTo(`/editEvent?id=${id}`, Object.keys(state).length > 0 ? { state } : {});
            },
            (error) => {
                this.enviando = false;
                if (this.unmounted) return;
                this.setState({ enviando: false, error: rejectionText(error), ocupadas: mesasOcupadasDelError(error) });
                // Lo más probable es que el salón haya cambiado mientras se llenaba el formulario
                // (una mesa que se ocupó): se vuelve a pedir para que las casillas digan la verdad.
                if (isStartsAt(this.state.startsAt)) this.cargarMesas(this.state.startsAt);
            },
        );
    }

    // ── Render ────────────────────────────────────────────────────────────────────────────
    campo(label, name, type, error, extra = {}, pie = null) {
        return (
            <div className="form-group">
                <label htmlFor={`private-event-${name}`}>{label}</label>
                <input
                    id={`private-event-${name}`}
                    type={type}
                    name={name}
                    className={`form-control${error ? ' is-invalid' : ''}`}
                    value={this.state[name]}
                    onChange={this.handleChange}
                    {...extra}
                />
                {error && <div className="invalid-feedback">{error}</div>}
                {pie}
            </div>
        );
    }

    renderSucursal(error) {
        if (this.sucursalElegida() || this.state.branches == null || this.state.branches.length === 1) return null;
        if (this.state.branches.length === 0) {
            return (
                <div className="form-group">
                    <label>Sucursal *</label>
                    <div className="invalid-feedback d-block">
                        No se pudieron cargar las sucursales{this.state.branchesError ? `: ${this.state.branchesError}` : ''}. Elegí una en la barra de arriba y recargá.
                    </div>
                </div>
            );
        }
        return (
            <div className="row">
                <div className="col-md-4">
                    <div className="form-group">
                        <label htmlFor="private-event-branchId">Sucursal *</label>
                        <select id="private-event-branchId" name="branchId" className={`form-control${error ? ' is-invalid' : ''}`}
                            value={this.state.branchId} onChange={this.handleChange}>
                            <option value="">Seleccionar…</option>
                            {this.state.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                        {error && <div className="invalid-feedback">{error}</div>}
                    </div>
                </div>
            </div>
        );
    }

    renderIncluye(error) {
        const { events, copying, copyError, copyFrom, copiedTitle } = this.state;
        const previa = includedPreview(this.state.rows, this.state.people);
        return (
            <>
                <h6 className="text-uppercase text-muted">Qué incluye *</h6>
                <small className="text-muted d-block mb-2">
                    Lo que la entrada trae sin cargo. De acá salen el reparto del pedido en el POS y «Sentar a todos».
                </small>
                {events != null && events.length === 0 ? (
                    <small className="text-muted d-block mb-2">
                        {this.state.eventsError
                            ? 'No se pudieron cargar los eventos anteriores: cargá los renglones a mano.'
                            : 'Todavía no hay eventos anteriores con «Qué incluye» para copiar.'}
                    </small>
                ) : (
                    <div className="form-group">
                        <select className="form-control private-event-copy" style={{ maxWidth: 420 }} aria-label="Copiar de un evento anterior"
                            value={copyFrom} disabled={events == null || copying} onChange={this.onChangeCopyFrom}>
                            <option value="">Copiar de un evento anterior…</option>
                            {(events || []).map((event) => <option key={event.id} value={event.id}>{event.label}</option>)}
                        </select>
                        {copying && <small className="text-muted d-block mt-1">Copiando…</small>}
                        {copiedTitle && !copying && !copyError && (
                            <small className="text-muted d-block mt-1 private-event-copied">Copiado de «{copiedTitle}». Podés cambiarlo acá abajo antes de crear.</small>
                        )}
                        {copyError && <small className="text-danger d-block mt-1">{copyError}</small>}
                    </div>
                )}
                <EventInclusionsEditor
                    hideHeading
                    value={this.state.rows}
                    onChange={this.onChangeRows}
                    menu={this.state.menu}
                    menuError={this.state.menuError}
                    onRetryMenu={this.reintentarMenu}
                    disabled={this.state.enviando || copying}
                />
                {previa && (
                    <p className="mb-2 private-event-included">
                        <strong>{previa.title}{previa.counted !== '' ? ':' : ''}</strong> {previa.counted}
                        {previa.notes !== '' && <span className="text-muted">{previa.counted !== '' ? ' · ' : ' '}También incluye: {previa.notes}</span>}
                    </p>
                )}
                {error && <div className="invalid-feedback d-block mb-2">{error}</div>}
            </>
        );
    }

    renderMesa = (table, elegidas) => {
        const libre = table.state === 'free';
        const lugares = Number(table.capacity) || 0;
        return (
            <div key={table.id} className="col-md-4 col-sm-6">
                <label className={`d-block mb-2 font-weight-normal${libre ? '' : ' text-muted'}`}>
                    <input
                        type="checkbox"
                        className="mr-2"
                        checked={elegidas.has(Number(table.id))}
                        disabled={!libre || this.state.cargandoMesas}
                        onChange={() => this.toggleTable(table.id)}
                    />
                    <b>{table.label}</b> · {lugares} {lugares === 1 ? 'lugar' : 'lugares'}{table.isVip ? ' · VIP' : ''}
                    {/* Los motivos son los del servidor, palabra por palabra. */}
                    {!libre && <small className="d-block">{table.reason || 'No disponible'}</small>}
                    {libre && table.nextReservation && (
                        <small className="d-block text-warning">
                            Ojo: tiene una reserva a las {table.nextReservation.startHour}:00
                            {table.nextReservation.name ? ` (${table.nextReservation.name})` : ''}
                        </small>
                    )}
                </label>
            </div>
        );
    }

    renderMesas(error) {
        const disp = this.state.disponibilidad;
        const conFecha = isStartsAt(this.state.startsAt);
        const elegidas = new Set(this.state.tableIds.map(Number));
        const resumen = disp ? tablesSummary(disp.tables, this.state.tableIds, this.state.people) : null;
        const hayLibres = !!disp && disp.tables.some((t) => t.state === 'free');
        const sinPersonas = peopleCount(this.state.people) == null;
        // Mientras se buscan las mesas de una fecha nueva, `disp` todavía es la de la anterior.
        const pasada = !this.state.cargandoMesas && jornadaPasada(disp);
        return (
            <>
                <h6 className="text-uppercase text-muted">Mesas *</h6>
                {!conFecha && <p className="text-muted mb-2">Con la fecha y hora cargadas aparecen acá las mesas libres, ya tildadas las que hacen falta.</p>}
                {conFecha && this.state.cargandoMesas && <p className="text-muted mb-2">Buscando mesas libres…</p>}
                {conFecha && this.state.errorMesas && (
                    <p className="text-danger mb-2">
                        No se pudieron consultar las mesas: {this.state.errorMesas}{' '}
                        <button type="button" className="btn btn-sm btn-white ml-1" onClick={() => this.cargarMesas(this.state.startsAt)}>Reintentar</button>
                    </p>
                )}
                {disp && pasada && (
                    <div className="alert alert-danger py-2 private-event-past" role="alert">
                        {PAST_DATE_TEXT} Cambiala para elegir las mesas.
                    </div>
                )}
                {disp && !pasada && disp.open === false && (
                    <div className="alert alert-danger py-2 private-event-closed" role="alert">
                        {String(disp.closedReason || '').replace(/[.\s]+$/, '')}. Con esa hora no se puede crear el evento: cambiala.
                    </div>
                )}
                {disp && !pasada && disp.open !== false && (
                    <>
                        {/* El día cerrado no impide la jornada (el evento es el motivo de abrir). */}
                        {disp.closedDay && (
                            <div className="alert alert-warning py-2" role="alert" style={{ fontSize: 13 }}>
                                El salón figura cerrado ese día. La jornada se puede armar igual.
                            </div>
                        )}
                        {disp.tables.length === 0 && <p className="text-muted mb-2">No hay mesas activas en el salón.</p>}
                        {disp.tables.length > 0 && !hayLibres && <p className="text-danger mb-2">No queda ninguna mesa libre a esa hora.</p>}
                        <div className="row private-event-tables">
                            {disp.tables.map((table) => this.renderMesa(table, elegidas))}
                        </div>
                        <p className="mb-2">
                            <strong className="private-event-seats">{resumen.text}</strong>
                            {sinPersonas && hayLibres && <span className="text-muted"> · cargá cuántas personas son y se tildan solas</span>}
                            {this.state.tablesTouched && (
                                <button type="button" className="btn btn-sm btn-white ml-2" onClick={this.proponerDeNuevo}>Proponer de nuevo</button>
                            )}
                        </p>
                        {resumen.warning && <p className="text-warning mb-2 private-event-tight">{resumen.warning}.</p>}
                        {this.state.mesasQuitadas.length > 0 && (
                            <p className="text-warning mb-2 private-event-dropped">
                                Ya no {this.state.mesasQuitadas.length === 1 ? 'está libre' : 'están libres'} a esa hora: {this.state.mesasQuitadas.join(', ')}. Se {this.state.mesasQuitadas.length === 1 ? 'destildó' : 'destildaron'}.
                            </p>
                        )}
                    </>
                )}
                {error && !(disp && disp.open === false) && <div className="invalid-feedback d-block mb-2">{error}</div>}
            </>
        );
    }

    renderCobro() {
        const later = this.state.paymentMode === 'later';
        return (
            <>
                <h6 className="text-uppercase text-muted">Cobro</h6>
                <div className="form-group mb-2">
                    <label className="d-block mb-1 font-weight-normal">
                        <input type="radio" className="mr-2" name="paymentMode" value="now" checked={!later} onChange={this.handleChange} />
                        Cobrar ahora
                    </label>
                    <label className="d-block mb-0 font-weight-normal">
                        <input type="radio" className="mr-2" name="paymentMode" value="later" checked={later} onChange={this.handleChange} />
                        Se cobra después
                    </label>
                </div>
                {later ? (
                    <small className="text-muted d-block mb-3">
                        Las entradas quedan impagas y se cobran después desde Ver asistentes → Registrar pago.
                    </small>
                ) : (
                    <div className="form-group">
                        <select name="paymentMethod" className="form-control" aria-label="Medio de pago" style={{ maxWidth: 240 }}
                            value={this.state.paymentMethod} onChange={this.handleChange}>
                            {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                    </div>
                )}
            </>
        );
    }

    renderQuienReserva(errors) {
        const later = this.state.paymentMode === 'later';
        const marca = later ? ' *' : '';
        return (
            <>
                <h6 className="text-uppercase text-muted">Quién reserva{marca}</h6>
                <small className="text-muted d-block mb-2">
                    {later
                        ? 'Hacen falta los cinco datos: es a quien se le cobra después.'
                        : 'Opcional: si lo dejás vacío, la venta va a consumidor final.'}
                </small>
                <div className="row">
                    <div className="col-md-4">
                        {this.campo(`Cédula${marca}`, 'cedula', 'text', errors.cedula, {
                            onChange: (e) => this.setState({ cedula: e.target.value, clientHint: null }),
                            onBlur: this.lookupCliente,
                        }, this.state.clientHint && (
                            <small className={`private-event-client ${this.state.clientHint.ok ? 'text-success' : 'text-muted'}`}>
                                {this.state.clientHint.text}
                            </small>
                        ))}
                    </div>
                    <div className="col-md-4">{this.campo(`Nombre${marca}`, 'customerName', 'text', errors.customerName)}</div>
                    <div className="col-md-4">{this.campo(`Apellido${marca}`, 'customerLastName', 'text', errors.customerLastName)}</div>
                    <div className="col-md-4">{this.campo(`Celular${marca}`, 'customerPhone', 'text', errors.customerPhone)}</div>
                    <div className="col-md-8">{this.campo(`Email${marca}`, 'customerEmail', 'email', errors.customerEmail)}</div>
                </div>
                {errors.customer && <div className="invalid-feedback d-block mb-2">{errors.customer}</div>}
            </>
        );
    }

    render() {
        const { enviando } = this.state;
        // Lo que falta se muestra recién después del primer intento, y desde ahí se va
        // limpiando solo a medida que se completa.
        const pendientes = this.state.intentado ? this.errores() : [];
        const errors = errorsByField(pendientes);
        const faltan = missingBlocks(pendientes);
        const total = totalText(this.state.people, this.state.price);
        const medio = (PAYMENT_METHODS.find((m) => m.value === this.state.paymentMethod) || {}).label;
        return (
            <div className="card card-small mb-4 private-event-form" ref={this.card}>
                {/* Mientras se envía no se toca nada: el <fieldset> apaga todos los controles. */}
                <fieldset className="card-body p-4" disabled={enviando}>
                    <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                        Para un cumpleaños o un festejo con todo pago. El evento nace publicado y sólo para el mostrador
                        (la web no lo abre ni lo vende), con sus mesas reservadas y las entradas ya registradas.
                    </p>
                    {this.renderSucursal(errors.branchId)}
                    <div className="row">
                        <div className="col-md-5">{this.campo('Nombre *', 'title', 'text', errors.title, { maxLength: LIMITS.title, placeholder: 'Cumple de Sofi' })}</div>
                        <div className="col-md-3">{this.campo('Fecha y hora *', 'startsAt', 'datetime-local', errors.startsAt, { onChange: this.onChangeStartsAt })}</div>
                        <div className="col-md-2">{this.campo('Personas *', 'people', 'number', errors.people, { min: 1, max: LIMITS.people, onChange: this.onChangePeople })}</div>
                        <div className="col-md-2">{this.campo('Precio por persona *', 'price', 'number', errors.price, { min: 0, step: 1 })}</div>
                    </div>

                    <hr />
                    {this.renderIncluye(errors.inclusions)}

                    <hr />
                    {this.renderMesas(errors.tables)}

                    <hr />
                    <div className="row">
                        <div className="col-md-4">{this.renderCobro()}</div>
                        <div className="col-md-8">{this.renderQuienReserva(errors)}</div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="private-event-notes">Nota interna</label>
                        <textarea id="private-event-notes" name="notes" className="form-control" rows="2"
                            placeholder="Para el equipo: quién trae la torta, a qué hora llegan, qué hay que preparar."
                            value={this.state.notes} onChange={this.handleChange} />
                    </div>

                    <hr />
                    {this.state.error && (
                        <div className="alert alert-danger private-event-error" role="alert">
                            {this.state.error}
                            {this.state.ocupadas.length > 0 && (
                                <ul className="mb-0 mt-2">
                                    {this.state.ocupadas.map((m, i) => (
                                        <li key={`${m.label}-${i}`}><b>{m.label}</b>: {m.reason}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                    {total && (
                        <p className="mb-2">
                            <strong className="private-event-total" style={{ fontSize: 18 }}>{total}</strong>
                            <span className="text-muted ml-2">
                                {this.state.paymentMode === 'later' ? 'se cobra después' : `se cobra ahora · ${medio}`}
                            </span>
                        </p>
                    )}
                    <button type="button" className="btn btn-accent" disabled={enviando} onClick={this.submit}>
                        {enviando ? 'Creando…' : 'Crear evento'}
                    </button>
                    {faltan.length > 0 && (
                        <span className="text-danger ml-3 private-event-missing">Falta revisar: {faltan.join(' · ')}</span>
                    )}
                </fieldset>
            </div>
        );
    }
}

// Cuánto espera el pedido de disponibilidad después del último cambio de fecha y hora.
FormPrivateEventComponent.availabilityDelayMs = 350;

export default FormPrivateEventComponent;
